import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as DropdownIcon } from "assets/svgs/dropdown.svg";
import { ReactComponent as CheckFilled } from "assets/svgs/check-filled.svg";
import useComponentClickOutside from "utils/hooks/use-component-click-outside";
import { ReactComponent as CheckUnfilled } from "assets/svgs/check-unfilled.svg";

const CustomDropdownProfession = ({
  value,
  options,
  onSelect,
  disabled,
  className,
  multiSelect,
  placeholder,
  compact = false,
}) => {
  const [selectedValue, setSelectedValue] = useState(
    multiSelect ? (value ?? []) : (value ?? "")
  );

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const inputRef = useRef(null);
  const { isComponentVisible, toggleChild, wrapRef } = useComponentClickOutside();

  // Group options by category
  const groupedOptions = options.reduce((acc, option) => {
    const category = option.category || "Other";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(option);
    return acc;
  }, {});

  // Filter options based on search term (across all categories)
  const filteredGroupedOptions = Object.entries(groupedOptions).reduce((acc, [category, professions]) => {
    const filtered = professions.filter(option =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {});

  const hasResults = Object.keys(filteredGroupedOptions).length > 0;

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isComponentVisible) {
      setSearchTerm("");
      setIsSearchMode(false);
    }
  }, [isComponentVisible]);

  // Focus input when entering search mode
  useEffect(() => {
    if (isSearchMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchMode]);

  const handleSelect = (value) => {
    setSelectedValue(value);
    setSearchTerm("");
    setIsSearchMode(false);
    toggleChild();
    onSelect(value);
  };

  const handleMultiSelect = (value) => {
    const prevSelected = [...selectedValue];
    if (selectedValue.includes(value)) {
      const result = prevSelected.filter((el) => el !== value);
      setSelectedValue([...result]);
      onSelect([...result]);
      return;
    }

    const result = [...prevSelected, value];
    setSelectedValue([...result]);
    onSelect([...result]);
  };

  const getPlaceHolder = () => {
    if (selectedValue === "" || (multiSelect && selectedValue.length === 0)) {
      return placeholder;
    }

    if (multiSelect) {
      return selectedValue
        .map((el) => options.find((ele) => ele.id === el)?.name)
        .filter(Boolean)
        .join(", ");
    }

    return options.find((ele) => ele.id === selectedValue)?.name || "";
  };

  const handleDropdownClick = (e) => {
    e.preventDefault();
    if (!isComponentVisible) {
      toggleChild();
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
    if (!isComponentVisible) {
      toggleChild();
    }
    setIsSearchMode(true);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSearchTerm("");
      setIsSearchMode(false);
      toggleChild();
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`relative ${className} ${disabled ? "select-none" : ""}`}
    >
      <div
        onClick={handleDropdownClick}
        className={`w-full relative flex justify-between items-center border border-gray-300 rounded-lg cursor-pointer ${compact ? "h-9 px-3" : "p-2 h-[43px] px-[15px]"}`}
      >
        {isComponentVisible && isSearchMode ? (
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onClick={handleInputClick}
            placeholder="Type to search..."
            className={`${compact ? "text-sm" : "text-sm"} mr-2 select-none bg-transparent flex-1`}
            disabled={disabled}
          />
        ) : (
          <span
            className={`${compact ? "text-sm" : "text-sm"} mr-2 select-none flex-1 cursor-pointer`}
            onClick={handleInputClick}
          >
            {getPlaceHolder()}
          </span>
        )}
        <DropdownIcon className="pointer-events-none" />
      </div>

      {isComponentVisible && (
        <div className="absolute w-full rounded-lg bg-white left-0 top-[60px] shadow-secondary-field z-10 text-black max-h-[220px] overflow-y-auto">
          {!hasResults ? (
            <div className="px-[18px] py-3 text-gray-500 italic">
              No professions found
            </div>
          ) : (
            Object.entries(filteredGroupedOptions).map(([category, professions], categoryIndex) => (
              <div key={category} className={categoryIndex > 0 ? "border-t border-gray-200" : ""}>
                {/* Category Header */}
                <div className="px-[18px] py-2 bg-primary sticky top-0 z-10">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {category}
                  </h3>
                </div>
                
                {/* Professions in this category */}
                <ul>
                  {professions.map(({ id, name }) => {
                    const isSelected = multiSelect && selectedValue.includes(id);
                    const cursorStyles =
                      disabled && !isSelected
                        ? "cursor-not-allowed hover:bg-transparent"
                        : "hover:bg-primary cursor-pointer";

                    return (
                      <li
                        key={id}
                        onClick={() => {
                          if (disabled && !isSelected) return;
                          multiSelect ? handleMultiSelect(id) : handleSelect(id);
                        }}
                        className={`px-[18px] pl-[26px] flex items-center gap-3 py-2.5 transition-all duration-100 ease-in-out ${cursorStyles} ${
                          isSelected ? "bg-primary" : "hover:bg-cyan"
                        }`}
                      >
                        {multiSelect && (
                          <>{isSelected ? <CheckFilled /> : <CheckUnfilled />}</>
                        )}
                        <span className="text-base">{name}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdownProfession;